import { Group } from "@mantine/core";
import { useDidUpdate } from "@mantine/hooks";
import { IconDownload } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { OptiSupportEndpoints } from "../../../urls";
import { downloadAsExcel } from "../../../utils/files/downloadAsExcel";
import { OptiSupportHelpLink } from "../../OptiSupportHelpLink/OptiSupportHelpLink";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import type {
  MeteringConceptInfo,
  OnboardingTodo,
  SiteOverviewSite,
  SiteStatusLogs,
  SiteStatusSite
} from "../site-status-types";
import type { SiteStatusTabs } from "../SiteStatus";
import { getClassificationColumn } from "./columns/getClassificationColumn";
import { getConsumptionAnalysisColumn } from "./columns/getConsumptionAnalysisColumn";
import { getCustomerColumn } from "./columns/getCustomerColumn";
import { getGridMeterSufficientColumn } from "./columns/getGridMeterSufficientColumn";
import { getMeteringConceptInfoColumn } from "./columns/getMeteringConceptInfoColumn";
import { getMeteringConceptTypeColumn } from "./columns/getMeteringConceptTypeColumn";
import { getObersterVertriebspartnerColumn } from "./columns/getObersterVertriebspartnerColumn";
import { getOnboardingColumn } from "./columns/getOnboardingColumn";
import { getOnboardingStatusColumn } from "./columns/getOnboardingStatusColumn";
import { getOrganizationsWithProjectAccessColumn } from "./columns/getOrganizationsWithProjectAccessColumn";
import { getProjectColumn } from "./columns/getProjectColumn";
import { getRequirementsMBKCreationColumn } from "./columns/getRequirementsMBKCreationColumn";
import { getSiteIdColumn } from "./columns/getSiteIdColumn";
import { getSiteLabelsColumn } from "./columns/getSiteLabelsColumn";
import { getSiteNameColumn } from "./columns/getSiteNameColumn";
import { getSubMeterDataIsSufficientColumn } from "./columns/getSubMeterDataIsSufficientColumn";
import { useSiteStatusTableState } from "./useSiteStatusTableState";
import { collectDataForExport } from "./utils/collectDataForExport";
import { DEFAULT_PAGE_SIZE } from "./utils/parseStoredPageSize";

interface SiteStatusTableProps {
  showCategoriesColumn?: boolean;
  showDemoProjects: boolean;
  showProjectLinks?: boolean;
  simple?: boolean;
  storageKey: string;
  tab: SiteStatusTabs;
  sites: Array<SiteOverviewSite | SiteStatusSite>;
  onClickLogsCell: (logs: SiteStatusLogs) => void;
  onClickOnboardingTodosCell: (todos: Array<OnboardingTodo>) => void;
  onClickMeteringConceptInfoCell: (mbkInfo: MeteringConceptInfo) => void;
}

function SiteStatusTable({
  showCategoriesColumn,
  showDemoProjects,
  showProjectLinks,
  simple,
  storageKey,
  tab,
  sites,
  onClickLogsCell,
  onClickOnboardingTodosCell,
  onClickMeteringConceptInfoCell
}: SiteStatusTableProps) {
  const { shouldShowStaffView } = useShouldShowStaffView();
  const defaultSorting = useMemo(
    () => (simple ? [{ id: "project", desc: false }] : []),
    [simple]
  );
  function handleClickExport() {
    const headers = mrtTable
      .getAllColumns()
      .filter((col) => !col.id.includes("mrt-"))
      .map((col) => col.columnDef.header);
    const dataRows = collectDataForExport(mrtTable);
    downloadAsExcel([headers, ...dataRows], "site-status");
  }

  function getDisplayedSitesCountText(
    sitesCount: number | null,
    totalSites: number
  ) {
    if (sitesCount === null) {
      return null;
    }

    return `${sitesCount} von ${totalSites} Liegenschaften`;
  }

  const columns = useMemo<
    Array<MRT_ColumnDef<SiteStatusSite | SiteOverviewSite>>
  >(() => {
    const classificationColumn = getClassificationColumn();
    const siteIdColumn = getSiteIdColumn();
    const siteNameColumn = getSiteNameColumn({ simple: !!simple });
    const projectColumn = getProjectColumn({
      showProjectLinks: !!showProjectLinks
    });

    const onboardingColumn = getOnboardingColumn();
    const gridDataSuffientColumn = getGridMeterSufficientColumn();
    const generatorDataSuffientColumn = getSubMeterDataIsSufficientColumn();
    const meteringConceptTypeColumn = getMeteringConceptTypeColumn();

    const customerColumn = getCustomerColumn();
    const organizationsWithProjectAccessColumn =
      getOrganizationsWithProjectAccessColumn();
    const obersterVertriebspartnerColumn = getObersterVertriebspartnerColumn();
    const onboardingStatusColumn = getOnboardingStatusColumn({
      onClickOnboardingTodosCell
    });
    const requirementsMbkCreationColumn = getRequirementsMBKCreationColumn();
    const consumptionAnalysisColumn = getConsumptionAnalysisColumn({
      onClickLogsCell
    });
    const meteringConceptInfoColumn = getMeteringConceptInfoColumn({
      onClickMeteringConceptInfoCell
    });
    const siteLabelsColumn = getSiteLabelsColumn();

    const simpleColumns = [
      ...(showCategoriesColumn ? [classificationColumn] : []),
      projectColumn,
      siteIdColumn,
      siteNameColumn,
      onboardingColumn,
      gridDataSuffientColumn,
      generatorDataSuffientColumn,
      meteringConceptTypeColumn
    ];
    const detailedColumns = [
      ...(showCategoriesColumn ? [classificationColumn] : []),
      siteIdColumn,
      siteNameColumn,
      projectColumn,
      obersterVertriebspartnerColumn,
      customerColumn,
      organizationsWithProjectAccessColumn,
      onboardingStatusColumn,
      requirementsMbkCreationColumn,
      meteringConceptTypeColumn,
      consumptionAnalysisColumn,
      meteringConceptInfoColumn,
      siteLabelsColumn
    ];
    const cols = simple ? simpleColumns : detailedColumns;

    return cols;
  }, [
    showProjectLinks,
    showCategoriesColumn,
    simple,
    onClickOnboardingTodosCell,
    onClickLogsCell,
    onClickMeteringConceptInfoCell
  ]);

  const tableState = useSiteStatusTableState({
    storageKey,
    tab,
    columns,
    defaultSorting
  });

  const mrtTable = useMantineReactTable<SiteStatusSite | SiteOverviewSite>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Keine Liegenschaften gefunden",
      searchTextInput: {
        placeholder: "Suche nach Projekt, Liegenschaft oder Kunde..."
      },
      initialState: {
        pagination: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
        sorting: defaultSorting,
        showColumnFilters: true,
        showGlobalFilter: true
      },
      hideBorder: true
    }),
    ...getMRTTopToolbarCustomActionsRenderer(),
    autoResetPageIndex: false,
    state: tableState.state,
    onPaginationChange: tableState.setPagination,
    onColumnFiltersChange: tableState.setColumnFilters,
    onGlobalFilterChange: tableState.setGlobalFilter,
    onSortingChange: tableState.setSorting,
    enableRowSelection: false,
    enableToolbarInternalActions: false,
    renderTopToolbarCustomActions: ({ table }) =>
      shouldShowStaffView ? (
        <Group gap="xl" justify="flex-end" w="auto">
          <IconButton
            color="primary"
            icon={IconDownload}
            size="xs"
            onClick={handleClickExport}
          >
            Als Excel-Datei herunterladen
          </IconButton>
          {getDisplayedSitesCountText(table.getRowCount(), sites.length)}
        </Group>
      ) : (
        <OptiSupportHelpLink
          optiSupportEndpoint={OptiSupportEndpoints.Meldedaten}
          text="Meldetypen in opti.node"
        />
      ),
    mantineTableBodyCellProps: { c: "neutral.9" },
    globalFilterFn: "includesString",
    enableColumnResizing: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: "subheader",
    enableFilterMatchHighlighting: false,
    columns: columns,
    data: sites
  });

  useDidUpdate(() => tableState.setPageIndex(0), [showDemoProjects]);

  return <MantineReactTable table={mrtTable} />;
}

export { SiteStatusTable };
